/**
 * VARIÁVEIS DE TEMPLATE META — FONTE ÚNICA (frontend).
 *
 * Espelho de
 * `whiz-server-2.0/src/modules/meta-templates/meta-template-variables.ts`.
 * O backend é quem decide (ele revalida tudo); este arquivo existe para o
 * editor guardar o usuário ANTES de enviar e para mostrar a pré-visualização
 * com valores de exemplo.
 *
 * Formato Meta: parâmetros NOMEADOS (`{{nome_da_variavel}}`,
 * `parameter_format: NAMED`).
 */

/**
 * Limite da Meta para o nome de um parâmetro nomeado. Estourar devolve
 * "Este modelo contém parâmetros de variáveis que excedem o comprimento máximo
 * de 20 caracteres" na criação do template — e o template inteiro é recusado.
 */
export const MAX_VARIABLE_NAME_LENGTH = 20;

/**
 * Variáveis AUTOMÁTICAS: o servidor resolve o valor sozinho no envio. Qualquer
 * outro nome válido é uma variável PERSONALIZADA, cujo valor é informado por
 * quem dispara a mensagem.
 */
export const TEMPLATE_VARIABLE_NAMES = ["nome_contato", "nome_whatsapp", "saudacao"] as const;

export type TemplateVariableName = (typeof TEMPLATE_VARIABLE_NAMES)[number];

/**
 * Nomes usados antes do limite de 20 caracteres ser respeitado. Só são LIDOS
 * (pré-visualização de um template antigo); nunca são oferecidos no menu de
 * variáveis nem inseridos em texto novo, e a validação pede a troca.
 */
export const LEGACY_VARIABLE_ALIASES: Readonly<Record<string, TemplateVariableName>> = {
    saudacao_dia_tarde_noite: "saudacao",
};

export interface TemplateVariableDef {
    name: TemplateVariableName;
    /** Rótulo curto (item do menu "Variáveis"). */
    label: string;
    /** Explicação exibida ao usuário. */
    description: string;
    /** Valor de exemplo — vai para `example.*_named_params` e para a pré-visualização. */
    example: string;
    /**
     * `true` quando a capitalização do valor depende da posição (a saudação).
     * Nome próprio não muda: “joão silva” no meio da frase seria errado.
     */
    sentenceCased: boolean;
    icon: string;
}

export const TEMPLATE_VARIABLES: readonly TemplateVariableDef[] = [
    {
        name: "nome_contato",
        label: "Nome do contato",
        description: "Trocada pelo nome do contato que vai receber a mensagem, no momento do envio.",
        example: "João Silva",
        sentenceCased: false,
        icon: "ri-user-line",
    },
    {
        name: "nome_whatsapp",
        label: "Nome no WhatsApp",
        description:
            "Trocada pelo nome que o contato usa no perfil do WhatsApp, e não pelo nome cadastrado "
            + "no Whiz. Sem esse nome, cai para o cadastrado.",
        example: "João",
        sentenceCased: false,
        icon: "ri-whatsapp-line",
    },
    {
        name: "saudacao",
        label: "Saudação do período",
        description:
            "Trocada por “bom dia”, “boa tarde” ou “boa noite” conforme a hora local da conexão. "
            + "Entra com inicial maiúscula quando começa uma frase.",
        example: "Bom dia",
        sentenceCased: true,
        icon: "ri-sun-line",
    },
] as const;

/**
 * Nome do catálogo com mais de 20 caracteres só é descoberto quando a Meta
 * recusa o template — longe daqui. Falhar na carga do módulo põe o erro no
 * build de quem acrescentou a variável.
 */
const nomesLongosDemais = TEMPLATE_VARIABLE_NAMES.filter(n => n.length > MAX_VARIABLE_NAME_LENGTH);
if (nomesLongosDemais.length)
    throw new Error(
        `Variáveis de template com nome acima de ${MAX_VARIABLE_NAME_LENGTH} caracteres `
        + `(a Meta recusa o template): ${nomesLongosDemais.join(", ")}.`,
    );

export const isTemplateVariableName = (name: string): name is TemplateVariableName =>
    (TEMPLATE_VARIABLE_NAMES as readonly string[]).includes(name);

/**
 * Nome canônico do catálogo para o que veio escrito entre as chaves, aceitando
 * os aliases legados. `undefined` = fora do catálogo.
 */
export const canonicalVariableName = (name: string): TemplateVariableName | undefined =>
    isTemplateVariableName(name) ? name : LEGACY_VARIABLE_ALIASES[name];

export const templateVariableDef = (name: TemplateVariableName): TemplateVariableDef =>
    TEMPLATE_VARIABLES.find(v => v.name === name)!;

/** Texto do placeholder, do jeito que entra no template. */
export const variablePlaceholder = (name: string): string => `{{${name}}}`;

// ---------------------------------------------------------------------------
// Variáveis personalizadas
// ---------------------------------------------------------------------------

/**
 * Nome aceito pela Meta em `parameter_name`: minúsculas, dígitos e underline,
 * começando por letra. Maiúscula, acento, hífen ou espaço fazem a Meta recusar
 * o template inteiro.
 */
export const CUSTOM_VARIABLE_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

/** Motivo pelo qual `name` NÃO serve como variável personalizada, ou `null` quando serve. */
export function customVariableNameError(name: string): string | null {
    const trimmed = name.trim();
    if (!trimmed) return "O nome da variável não pode ficar vazio.";
    if (isTemplateVariableName(trimmed) || trimmed in LEGACY_VARIABLE_ALIASES)
        return `"${trimmed}" é uma variável automática do sistema — escolha outro nome.`;
    if (trimmed.length > MAX_VARIABLE_NAME_LENGTH)
        return `"${trimmed}" tem mais de ${MAX_VARIABLE_NAME_LENGTH} caracteres (limite da Meta).`;
    if (!CUSTOM_VARIABLE_NAME_PATTERN.test(trimmed))
        return `"${trimmed}" é inválido — use apenas letras minúsculas sem acento, números e underline, começando por letra.`;
    return null;
}

/** `true` quando o nome serve como variável personalizada. */
export const isCustomVariableName = (name: string): boolean => customVariableNameError(name) === null;

/** Ocorrências que NÃO são automáticas — as personalizadas e as malformadas. */
export function extractCustomOccurrences(text?: string | null): TemplateVariableOccurrence[] {
    return extractVariableOccurrences(text).filter(o => !canonicalVariableName(o.name));
}

/** Nomes personalizados VÁLIDOS usados no texto, na ordem de aparição e sem repetir. */
export function extractCustomVariableNames(text?: string | null): string[] {
    const seen: string[] = [];
    for (const occurrence of extractCustomOccurrences(text))
        if (isCustomVariableName(occurrence.name) && !seen.includes(occurrence.name)) seen.push(occurrence.name);
    return seen;
}

/**
 * Exemplo usado quando ninguém informou um. A Meta exige um valor de exemplo
 * não vazio por parâmetro nomeado; o nome humanizado é melhor do que um
 * literal genérico na hora da revisão.
 */
export const defaultExampleForCustomVariable = (name: string): string => name.replaceAll("_", " ").trim() || name;

/**
 * Sugere um nome a partir do que o usuário digitou: minúsculas sem acento,
 * espaço/hífen viram underline, o resto cai fora. É o que faz "Código do
 * Pedido" virar `codigo_do_pedido` enquanto se digita.
 */
export function sugerirNomeDeVariavel(raw: string): string {
    return raw
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/^[^a-z]+/, "")
        .slice(0, MAX_VARIABLE_NAME_LENGTH);
}

export type TemplateTextComponent = "header" | "body" | "footer";

const COMPONENT_LABEL: Record<TemplateTextComponent, string> = {
    header: "cabeçalho",
    body: "corpo",
    footer: "rodapé",
};

/** Quantas variáveis a Meta aceita por componente de texto. */
export const MAX_VARIABLES_PER_COMPONENT: Record<TemplateTextComponent, number> = {
    header: 1,
    body: Number.POSITIVE_INFINITY,
    footer: 0,
};

/**
 * Tamanho máximo do texto de cada componente, pela Meta. O `maxlength` do campo
 * cobre a digitação, mas NÃO a inserção de variável pelo botão (que escreve no
 * v-model) nem um template já gravado maior que o limite — daí o valor viver
 * aqui, onde as duas checagens conseguem lê-lo.
 */
export const MAX_COMPONENT_TEXT_LENGTH: Record<TemplateTextComponent, number> = {
    header: 60,
    body: 1024,
    footer: 60,
};

const VARIABLE_PATTERN = /\{\{\s*([^{}]*?)\s*\}\}/g;

export interface TemplateVariableOccurrence {
    name: string;
    start: number;
    end: number;
}

export function extractVariableOccurrences(text?: string | null): TemplateVariableOccurrence[] {
    if (!text) return [];
    const found: TemplateVariableOccurrence[] = [];
    const pattern = new RegExp(VARIABLE_PATTERN.source, "g");
    for (const match of text.matchAll(pattern))
        found.push({
            name: (match[1] ?? "").trim(),
            start: match.index ?? 0,
            end: (match.index ?? 0) + match[0].length,
        });
    return found;
}

/** Ocorrência que casa com o catálogo: `name` como está no texto, `canonical` do catálogo. */
export interface SupportedVariableOccurrence extends TemplateVariableOccurrence {
    canonical: TemplateVariableName;
}

/** Ocorrências que o catálogo reconhece, incluindo as escritas com nome legado. */
export function extractSupportedOccurrences(text?: string | null): SupportedVariableOccurrence[] {
    const found: SupportedVariableOccurrence[] = [];
    for (const occurrence of extractVariableOccurrences(text)) {
        const canonical = canonicalVariableName(occurrence.name);
        if (canonical) found.push({ ...occurrence, canonical });
    }
    return found;
}

/** Nomes canônicos usados no texto, na ordem de aparição e sem repetir. */
export function extractSupportedVariables(text?: string | null): TemplateVariableName[] {
    const seen: TemplateVariableName[] = [];
    for (const occurrence of extractSupportedOccurrences(text))
        if (!seen.includes(occurrence.canonical)) seen.push(occurrence.canonical);
    return seen;
}

export const textUsesTemplateVariables = (text?: string | null): boolean =>
    extractSupportedVariables(text).length > 0;

/**
 * `true` se o texto usa qualquer variável válida — automática ou personalizada.
 * É esta que decide se o componente é `TEXT_NAMED_PARAMETER(S)` na Meta.
 */
export const textUsesAnyVariables = (text?: string | null): boolean =>
    textUsesTemplateVariables(text) || extractCustomVariableNames(text).length > 0;

/**
 * A variável já está no texto? Automática compara pelo nome CANÔNICO (um
 * placeholder legado já ocupa o lugar do novo); personalizada compara pelo nome
 * escrito, que é o que a Meta casa com o placeholder.
 */
export const textContainsVariable = (text: string | null | undefined, name: string): boolean => {
    const canonical = canonicalVariableName(name);
    if (canonical) return extractSupportedOccurrences(text).some(o => o.canonical === canonical);
    return extractVariableOccurrences(text).some(o => o.name === name);
};

/**
 * Regras de posicionamento da Meta para UM componente de texto
 * (V-1…V-7 da spec `meta-template-variaveis`). Array vazio = válido.
 */
export function validateVariablesInText(text: string | null | undefined, component: TemplateTextComponent): string[] {
    const errors: string[] = [];
    const occurrences = extractVariableOccurrences(text);
    if (!occurrences.length) return errors;

    const label = COMPONENT_LABEL[component];
    const content = text ?? "";

    if (MAX_VARIABLES_PER_COMPONENT[component] === 0) {
        errors.push(`O ${label} não aceita variáveis.`);
        return errors;
    }

    // O que não é automático é personalizado, e só precisa passar pelas regras
    // de nomenclatura da Meta. Nome legado sai com mensagem própria: a correção
    // é uma troca mecânica que o usuário não adivinha sozinho.
    const naoCanonicos = [...new Set(occurrences.map(o => o.name).filter(n => !isTemplateVariableName(n)))];
    const legados = naoCanonicos.filter(n => n in LEGACY_VARIABLE_ALIASES);
    const invalidos = naoCanonicos
        .filter(n => !(n in LEGACY_VARIABLE_ALIASES))
        .map(n => customVariableNameError(n))
        .filter((motivo): motivo is string => motivo !== null);

    if (legados.length)
        errors.push(
            `Variável com nome antigo no ${label}: `
            + `${legados.map(n => `{{${n}}} (use {{${LEGACY_VARIABLE_ALIASES[n]}}})`).join(", ")}. `
            + `A Meta recusa nomes com mais de ${MAX_VARIABLE_NAME_LENGTH} caracteres.`,
        );

    if (invalidos.length)
        errors.push(`Nome de variável inválido no ${label}: ${invalidos.join(" ")}`);

    const limit = MAX_VARIABLES_PER_COMPONENT[component];
    if (occurrences.length > limit)
        errors.push(`O ${label} aceita no máximo ${limit} variável(is).`);

    const duplicated = [...new Set(occurrences.map(o => o.name).filter((n, i, all) => all.indexOf(n) !== i))];
    if (duplicated.length)
        errors.push(`A mesma variável não pode se repetir no ${label}: ${duplicated.map(n => `{{${n}}}`).join(", ")}.`);

    const first = occurrences[0];
    const last = occurrences[occurrences.length - 1];
    if (!content.slice(0, first.start).trim())
        errors.push(`O ${label} não pode começar com uma variável — escreva algum texto antes dela.`);
    if (!content.slice(last.end).trim())
        errors.push(`O ${label} não pode terminar com uma variável — escreva algum texto depois dela.`);

    for (let i = 1; i < occurrences.length; i++)
        if (!content.slice(occurrences[i - 1].end, occurrences[i].start).trim()) {
            errors.push(`Duas variáveis não podem ficar lado a lado no ${label} — escreva algum texto entre elas.`);
            break;
        }

    return errors;
}

// ---------------------------------------------------------------------------
// Inserção guiada (botão "Variáveis")
// ---------------------------------------------------------------------------

export interface InsertionCheck {
    allowed: boolean;
    /** Motivo da recusa, para o tooltip do item desabilitado. */
    reason?: string;
}

/**
 * Uma variável pode ser inserida naquele componente? Cobre as recusas que o
 * usuário consegue enxergar de antemão: já está no texto, componente não aceita
 * variável, e limite do componente já atingido.
 *
 * As regras de POSIÇÃO (início/fim/adjacência) não entram aqui: a inserção
 * acontece no cursor e o texto pode ser corrigido depois — quem reclama delas é
 * a validação ao vivo, que bloqueia o salvamento.
 */
export function canInsertVariable(
    text: string | null | undefined,
    component: TemplateTextComponent,
    name: string,
): InsertionCheck {
    const label = COMPONENT_LABEL[component];
    const limit = MAX_VARIABLES_PER_COMPONENT[component];

    if (limit === 0) return { allowed: false, reason: `O ${label} não aceita variáveis.` };
    if (textContainsVariable(text, name)) return { allowed: false, reason: `Esta variável já está no ${label}.` };

    const total = extractVariableOccurrences(text).length;
    if (total >= limit) return { allowed: false, reason: `O ${label} aceita no máximo ${limit} variável(is).` };

    // O `maxlength` do campo não vê a inserção pelo botão. Conta o pior caso
    // (`insertVariableAt` pode acrescentar um espaço de cada lado).
    const limiteTexto = MAX_COMPONENT_TEXT_LENGTH[component];
    if ((text?.length ?? 0) + variablePlaceholder(name).length + 2 > limiteTexto)
        return {
            allowed: false,
            reason: `Não cabe: o ${label} aceita até ${limiteTexto} caracteres.`,
        };

    return { allowed: true };
}

export interface InsertionResult {
    text: string;
    /** Posição do cursor depois da inserção (fim do placeholder). */
    caret: number;
}

/**
 * Insere o placeholder na posição do cursor. Acrescenta um espaço de cada lado
 * quando o vizinho não é espaço nem pontuação — sem isso o usuário cria
 * facilmente `Olá{{nome_contato}}`, que a Meta lê como uma palavra colada.
 */
export function insertVariableAt(
    text: string,
    caret: number,
    name: string,
): InsertionResult {
    const safeCaret = Math.max(0, Math.min(caret, text.length));
    const before = text.slice(0, safeCaret);
    const after = text.slice(safeCaret);

    const placeholder = variablePlaceholder(name);
    const precisaEspacoAntes = before.length > 0 && !/\s$/.test(before);
    const precisaEspacoDepois = after.length > 0 && !/^[\s.,;:!?)\]}]/.test(after);

    const inserted = `${precisaEspacoAntes ? " " : ""}${placeholder}${precisaEspacoDepois ? " " : ""}`;

    return {
        text: `${before}${inserted}${after}`,
        caret: safeCaret + inserted.length - (precisaEspacoDepois ? 1 : 0),
    };
}

// ---------------------------------------------------------------------------
// Pré-visualização
// ---------------------------------------------------------------------------

export function startsSentenceAt(text: string, index: number): boolean {
    const before = text.slice(0, index);
    if (!before.trim()) return true;
    const semEspacos = before.replace(/[^\S\r\n]+$/, "");
    return /[.!?:;\r\n]$/.test(semEspacos);
}

const capitalizeFirst = (value: string): string =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

/**
 * Substitui as variáveis pelos valores de EXEMPLO, respeitando a capitalização
 * ciente da posição — é o que a pré-visualização mostra. As personalizadas usam
 * o exemplo informado pelo usuário; sem ele, o nome humanizado.
 */
export function previewWithExampleValues(
    text: string | null | undefined,
    customExamples: Readonly<Record<string, string>> = {},
): string {
    const content = text ?? "";
    return content.replace(new RegExp(VARIABLE_PATTERN.source, "g"), (whole, rawName: string, offset: number) => {
        const name = (rawName ?? "").trim();
        const canonical = canonicalVariableName(name);

        if (!canonical) {
            if (!isCustomVariableName(name)) return whole;
            return customExamples[name]?.trim() || defaultExampleForCustomVariable(name);
        }

        const { example, sentenceCased } = templateVariableDef(canonical);
        if (!sentenceCased) return example;
        return startsSentenceAt(content, offset) ? capitalizeFirst(example) : example.toLocaleLowerCase("pt-BR");
    });
}

/**
 * Exemplos exigidos pela Meta na criação, na ordem de aparição — automáticas e
 * personalizadas. O `param_name` é o nome ESCRITO no texto, que é o que a Meta
 * casa com o placeholder.
 */
export function buildNamedExamplesForText(
    text: string | null | undefined,
    customExamples: Readonly<Record<string, string>> = {},
): { param_name: string; example: string }[] {
    const examples: { param_name: string; example: string }[] = [];
    for (const occurrence of extractVariableOccurrences(text)) {
        if (examples.some(e => e.param_name === occurrence.name)) continue;

        const canonical = canonicalVariableName(occurrence.name);
        if (canonical) {
            examples.push({ param_name: occurrence.name, example: templateVariableDef(canonical).example });
            continue;
        }

        if (!isCustomVariableName(occurrence.name)) continue;
        examples.push({
            param_name: occurrence.name,
            example: customExamples[occurrence.name]?.trim() || defaultExampleForCustomVariable(occurrence.name),
        });
    }
    return examples;
}
